import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelProductBenefits,
  getProductBenefitsPersonalData,
  patchProductBenefitsContract,
  updateProductBenefitsContract,
  updateProductBenefitsInsurances,
} from '../api/productBenefits/getProductBenefits';
import { ProfileBenefitType, ProfileBenefitsUserData, StartContractRequest } from '../@types/productBenefits';

export const useGetProductBenefitsPersonalData = () => {
  const { isLoading, isFetching, data, isError } = useQuery({
    queryKey: ['product-benefits-personal-data'],
    queryFn: getProductBenefitsPersonalData,
  });

  return {
    initializing: isLoading || isFetching || isError,
    personalData: data,
  };
};

export const useProductBenefitsStartContract = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isSuccess, isLoading } = useMutation(
    async ({ benefitType, formData }: { benefitType: ProfileBenefitType; formData: ProfileBenefitsUserData }) => {
      const data = await updateProductBenefitsContract(benefitType, formData);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['product-benefits-personal-data']);
        queryClient.invalidateQueries(['products-status']);
      },
    },
  );
  return { mutate: mutateAsync, isSuccess, isLoading };
};

export const useProductBenefitsModifyData = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isSuccess, isLoading } = useMutation(
    async (formData: StartContractRequest) => {
      const data = await patchProductBenefitsContract(formData);
      return data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['product-benefits-personal-data']),
    },
  );
  return { mutate: mutateAsync, isSuccess, isLoading };
};

export const useProductBenefitsGrantBenefit = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isSuccess, isLoading } = useMutation(
    async (benefitType: ProfileBenefitType) => {
      const data = await updateProductBenefitsInsurances(benefitType);
      return data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['products-status']),
    },
  );
  return { mutate: mutateAsync, isSuccess, isLoading };
};

export const useProductBenefitsCancelBenefit = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isSuccess, isLoading } = useMutation(
    async (benefitType: ProfileBenefitType) => {
      const data = await cancelProductBenefits(benefitType);
      return data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['products-status']),
      onError: () => queryClient.invalidateQueries(['products-status']),
    },
  );
  return { mutate: mutateAsync, isSuccess, isLoading };
};
